import { computed, observable, makeObservable, actionBound } from "mobx";
import { AggregatedOrderProduct, AggregatedOrderProductCombineStatus, AggregatedOrderProductSortInfo } from "../type";
import { compareAggregatedOrderProductByBackendSortIndex } from "../ProductFilterSortUtils";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderStatus from "@/enum/OrderStatus";
import { buildOrderItemTag } from "@/utils/OrderUtils";
import { isEmpty } from "@/utils";
import { createContext } from "react";
import OrderSkuQuantity from "@/enum/OrderSkuQuantity";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";

export function buildAggregatedOrderProductId(productKey: string, shopIds: number[]) {
    shopIds = [...shopIds].sort();

    /** 不同平台可能存在相同的originalId，添加前缀避免重复 */
    return shopIds.join("-") + "-" + productKey;
}

export interface AggregatedOrderProductSelectState {
    isFullSelected: boolean;
    isPartSelected: boolean;
    selectedCount: number;
}

export enum ProductMergeRule {
    SAME_PRODUCT_SKU = "SAME_PRODUCT_SKU",
    TITLE_AND_PROPERTIES = "TITLE_AND_PROPERTIES",
    SKU_EXTERNAL_ID = "SKU_EXTERNAL_ID",
    SKU_SIMPLE_TITLE = "SKU_SIMPLE_TITLE",
}

export enum ProductInfoSortStrategy {
    QUANTITY_DESC = "QUANTITY_DESC",
    SAME_PRODUCT = "SAME_PRODUCT",
}

const PRODUCT_MERGE_RULE = "__PRODUCT_MERGE_RULE__";
const LEGACY_PRODUCT_MERGE_OPTIONS = "__PRODUCT_MERGE_OPTIONS__";
const PRODUCT_INFO_SORT_STRATEGY = "__PRODUCT_INFO_SORT_STRATEGY__";

function getProductMergeRuleFromLegacyOptions(options: unknown): ProductMergeRule | null {
    if (!Array.isArray(options)) return null;

    if (options.includes("TITLE") && options.includes("SKU_PROPERTIES")) {
        return ProductMergeRule.TITLE_AND_PROPERTIES;
    }

    if (options.includes("SKU_EXTERNAL_ID")) {
        return ProductMergeRule.SKU_EXTERNAL_ID;
    }

    if (options.includes("SKU_SIMPLE_TITLE")) {
        return ProductMergeRule.SKU_SIMPLE_TITLE;
    }

    return options.length ? null : ProductMergeRule.SAME_PRODUCT_SKU;
}

export function getProductFilterOrderItemStatus(tab: OrderTab) {
    return tab === OrderTab.SHIPPED ? OrderStatus.SHIPPED : OrderStatus.PENDING_SHIPMENT;
}

function getOrderItemSortInfo(orderItem: OrderItemDTO): AggregatedOrderProductSortInfo {
    return {
        productExternalId: orderItem.productExternalId,
        productOriginalId: orderItem.productOriginalId,
        title: orderItem.title,
        localSkuProperties: orderItem.localSkuProperties,
        skuProperties: orderItem.skuProperties,
        skuExternalId: orderItem.skuExternalId,
        skuOriginalId: orderItem.skuOriginalId,
    };
}

class ProductFilterStore {
    constructor() {
        makeObservable(this, {
            selectStateMapping: observable,
            mergeRule: observable,
            productInfoSortStrategy: observable,
            aggregatedOrderProductMapping: observable,
            aggregatedOrderProducts: observable,
            orderItemProductMapping: computed,
            productFilterOrderSortTypes: observable,
            setProductFilterOrderSortTypes: actionBound,
            setProductInfoSortStrategy: actionBound,
            setSelectStateMapping: actionBound,
            calcAggregatedOrderProducts: actionBound,
            updateMergeRule: actionBound,
            buildMergedKey: actionBound,
        });
        const mergeRuleCache = localStorage.getItem(PRODUCT_MERGE_RULE);
        let hasMergeRuleCache = false;

        if (mergeRuleCache) {
            try {
                const mergeRule = JSON.parse(mergeRuleCache);

                if (Object.values(ProductMergeRule).includes(mergeRule)) {
                    this.mergeRule = mergeRule;
                    hasMergeRuleCache = true;
                }
            } catch {}
        }

        if (!hasMergeRuleCache) {
            const legacyMergeOptionsCache = localStorage.getItem(LEGACY_PRODUCT_MERGE_OPTIONS);

            if (legacyMergeOptionsCache) {
                try {
                    const legacyMergeOptions = JSON.parse(legacyMergeOptionsCache);
                    const mergeRule = getProductMergeRuleFromLegacyOptions(legacyMergeOptions);

                    if (mergeRule) {
                        this.mergeRule = mergeRule;
                        localStorage.setItem(PRODUCT_MERGE_RULE, JSON.stringify(mergeRule));
                        localStorage.removeItem(LEGACY_PRODUCT_MERGE_OPTIONS);
                    }
                } catch {}
            }
        }

        const productInfoSortStrategyCache = localStorage.getItem(PRODUCT_INFO_SORT_STRATEGY);

        if (productInfoSortStrategyCache) {
            try {
                const productInfoSortStrategy = JSON.parse(productInfoSortStrategyCache);

                if (Object.values(ProductInfoSortStrategy).includes(productInfoSortStrategy)) {
                    this.productInfoSortStrategy = productInfoSortStrategy;
                }
            } catch {}
        }
    }

    selectStateMapping = new Map<string, AggregatedOrderProductSelectState>();

    mergeRule: ProductMergeRule = ProductMergeRule.SAME_PRODUCT_SKU;

    productInfoSortStrategy: ProductInfoSortStrategy = ProductInfoSortStrategy.QUANTITY_DESC;

    /**
     * 全量商品映射表
     *
     * 数据特性：
     * 1. 包含所有处理阶段的商品，包括：
     *    - 原始商品（combineStatus = NORMAL）
     *    - 合并生成的父商品（combineStatus = PARENT）
     *    - 被合并的子商品（combineStatus = CHILD）
     * 2. 与 aggregatedOrderProducts 的关系：本映射表包含后者所有元素及更多中间状态商品
     */
    aggregatedOrderProductMapping = new Map<string, AggregatedOrderProduct>();

    /**
     * 商品列表
     *
     * 数据特性：
     * 1. 经过合并处理后的最终态商品集合，已过滤掉被合并的子商品
     * 2. 包含两种类型商品：
     *    - 独立商品（combineStatus = NORMAL）
     *    - 合并后的父商品（combineStatus = PARENT）
     * 3. 按商品总量降序排列，用于界面展示
     *
     * 与 mapping 的关系：
     * - 本列表是 mapping 的子集（filtered + sorted）
     * - 通过 mapping 可以查找列表中商品的完整关联数据
     */
    aggregatedOrderProducts: AggregatedOrderProduct[] = [];

    /**
     * 订单项到商品的映射
     *
     *  映射规则：
     * - Key: 订单项ID(orderItem.id)
     * - Value: 该订单项所属的商品（可能为父商品或独立商品）
     *
     * 3. 一个订单项只会出现在【aggregatedOrderProduct】中的一个商品（可能为父商品或独立商品）中
     */
    get orderItemProductMapping() {
        const map = new Map<number, AggregatedOrderProduct>();

        for (const aggregatedOrderProduct of this.aggregatedOrderProducts) {
            aggregatedOrderProduct.orderItemIds.forEach((orderItemId) => {
                map.set(orderItemId, aggregatedOrderProduct);
            });
        }

        return map;
    }

    productFilterOrderSortTypes: OrderSkuQuantity[] = [];

    setProductFilterOrderSortTypes(productFilterOrderSortTypes: OrderSkuQuantity[]) {
        this.productFilterOrderSortTypes = productFilterOrderSortTypes;
    }

    setProductInfoSortStrategy(productInfoSortStrategy: ProductInfoSortStrategy) {
        this.productInfoSortStrategy = productInfoSortStrategy;
        localStorage.setItem(PRODUCT_INFO_SORT_STRATEGY, JSON.stringify(productInfoSortStrategy));
        this.aggregatedOrderProducts = this.sortAggregatedOrderProducts(this.aggregatedOrderProducts);
    }

    setSelectStateMapping(selectStateMapping: Map<string, AggregatedOrderProductSelectState>) {
        this.selectStateMapping = selectStateMapping;
    }

    compareByProductInfo(a: AggregatedOrderProduct, b: AggregatedOrderProduct) {
        return compareAggregatedOrderProductByBackendSortIndex(a, b);
    }

    sortAggregatedOrderProducts(products: AggregatedOrderProduct[]) {
        products.forEach((product) => {
            if (product.childProducts?.length) {
                product.childProducts = [...product.childProducts].sort(this.compareByProductInfo);
            }
        });

        return [...products].sort((a, b) => {
            if (this.productInfoSortStrategy === ProductInfoSortStrategy.SAME_PRODUCT) {
                return this.compareByProductInfo(a, b);
            }

            return b.quantity - a.quantity;
        });
    }

    /**
     * 计算商品：将原始订单数据转换为商品筛选的结构
     *
     * 执行阶段：
     * 1. 初始化阶段：遍历所有订单项，构建基础商品结构
     * 2. 合并阶段：根据mergedKey合并到父商品中
     * 3. 清理阶段：移除被合并的子商品，生成最终列表
     */
    calcAggregatedOrderProducts(orders: OrderDTO[], orderItemStatus = OrderStatus.PENDING_SHIPMENT) {
        // 临时存储容器
        const tempProductMap = new Map<string, AggregatedOrderProduct>();
        const processingProducts: AggregatedOrderProduct[] = [];

        /* 阶段一：构建基础商品数据 ---------------------------- */
        orders?.forEach((order) => {
            order.orderItems.forEach((orderItem) => {
                // 仅聚合当前商品筛选入口支持的商品状态和非赠品
                if (orderItem.status !== orderItemStatus || orderItem.isGift || orderItem.isRefunding) return;

                // 生成关键标识
                const mergedKey = this.buildMergedKey(orderItem); // 合并依据键
                const uniqProductKey = buildOrderItemTag(orderItem); // 唯一识别键
                const compositeId = buildAggregatedOrderProductId(uniqProductKey, [order.shopId]); // 复合ID

                // 处理商品聚合
                let currentProduct: AggregatedOrderProduct;
                if (!tempProductMap.has(compositeId)) {
                    // 新建记录
                    currentProduct = {
                        id: compositeId,
                        shopId: order.shopId,
                        quantity: orderItem.quantity,
                        title: orderItem.title,
                        skuProperties: orderItem.skuProperties,
                        picture: orderItem.picture,
                        simpleTitle: orderItem.simpleTitle,
                        sortInfo: getOrderItemSortInfo(orderItem),
                        mergedKey,
                        uniqProductKey,
                        orderIds: [],
                        orderItemIds: [],
                        combineStatus: AggregatedOrderProductCombineStatus.NORMAL,
                    };
                    tempProductMap.set(compositeId, currentProduct);
                    processingProducts.push(currentProduct);
                } else {
                    // 已有商品数量累加
                    currentProduct = tempProductMap.get(compositeId)!;
                    currentProduct.quantity += orderItem.quantity;
                }

                currentProduct.orderIds = [...new Set([...currentProduct.orderIds, order.id])];
                currentProduct.orderItemIds = [...new Set([...currentProduct.orderItemIds, orderItem.id])];
            });
        });

        /* 阶段二：合并相同mergedKey的商品 ---------------------- */
        const mergedIds: string[] = []; // 记录被合并的ID
        const mergeParentProductMap = new Map<string, AggregatedOrderProduct>(); // 记录生成的合并父商品

        tempProductMap.forEach((baseProduct) => {
            if (isEmpty(baseProduct.mergedKey)) return;

            // 已经合并
            if (mergeParentProductMap.has(baseProduct.mergedKey)) return;

            const sameKeyProducts = processingProducts.filter((p) => p.mergedKey === baseProduct.mergedKey);

            // 无需合并的情况
            if (sameKeyProducts.length <= 1) return;

            // 记录待删除ID
            mergedIds.push(...sameKeyProducts.map((p) => p.id));

            // 构建父级商品
            const parentProduct = this.createAggregatedOrderParentProduct(sameKeyProducts, baseProduct.mergedKey);

            // 新建记录
            mergeParentProductMap.set(baseProduct.mergedKey, parentProduct);
        });

        for (const [, mergeParentProduct] of mergeParentProductMap) {
            processingProducts.push(mergeParentProduct);
            tempProductMap.set(mergeParentProduct.id, mergeParentProduct);
        }

        /* 阶段三：数据清理与最终处理 -------------------------- */
        // 移除被合并的子商品
        const finalProducts = processingProducts.filter((p) => !mergedIds.includes(p.id));

        // 提交状态更新
        this.aggregatedOrderProductMapping = tempProductMap;
        this.aggregatedOrderProducts = this.sortAggregatedOrderProducts(finalProducts);
    }

    /** 创建父级组合商品 */
    createAggregatedOrderParentProduct(children: AggregatedOrderProduct[], mergedKey: string): AggregatedOrderProduct {
        // 生成唯一标识（排序子商品key防重复）
        const sortedKeys = children.map((c) => c.uniqProductKey);
        const compositeKey = sortedKeys.join("+");
        const childProducts = children.map((child) => ({
            ...child,
            combineStatus: AggregatedOrderProductCombineStatus.CHILD,
        }));
        const sortedChildProducts = [...childProducts].sort(this.compareByProductInfo);

        // 提取跨店铺信息
        const shopIds = [...new Set(children.flatMap((c) => (c.shopId ? [c.shopId] : c.shopIds || [])))];
        const combinedOrderIds = [...new Set(children.flatMap(({ orderIds }) => orderIds || []))];
        const combinedOrderItemIds = [...new Set(children.flatMap(({ orderItemIds }) => orderItemIds || []))];

        return {
            id: buildAggregatedOrderProductId(compositeKey, shopIds),
            shopIds,
            quantity: children.reduce((sum, c) => sum + c.quantity, 0),
            title: children[0].title,
            skuProperties: children[0].skuProperties,
            picture: children[0].picture,
            simpleTitle: children[0].simpleTitle,
            sortInfo: sortedChildProducts[0]?.sortInfo,
            mergedKey,
            childProducts: sortedChildProducts,
            uniqProductKey: compositeKey,
            orderIds: combinedOrderIds,
            orderItemIds: combinedOrderItemIds,
            combineStatus: AggregatedOrderProductCombineStatus.PARENT,
        };
    }

    updateMergeRule(rule: ProductMergeRule) {
        this.mergeRule = rule;
        localStorage.setItem(PRODUCT_MERGE_RULE, JSON.stringify(rule));
    }

    buildMergedKey(orderItem: OrderItemDTO) {
        const { simpleTitle, title, localSkuProperties, skuProperties, skuExternalId } = orderItem;

        switch (this.mergeRule) {
            case ProductMergeRule.TITLE_AND_PROPERTIES:
                return `${simpleTitle || title}${localSkuProperties || skuProperties}`;
            case ProductMergeRule.SKU_EXTERNAL_ID:
                return skuExternalId?.trim() || null;
            case ProductMergeRule.SKU_SIMPLE_TITLE:
                return simpleTitle?.trim() || null;
            default:
                return null;
        }
    }
}

export const ProductFilterStoreContext = createContext<ProductFilterStore>(undefined);

export default ProductFilterStore;
